// src/components/CommsPage.jsx
// Comms — Quinn's unified communications workspace.
// Six tabs: Inbox | Drafts | Sequences | Campaigns | Sent | Cadence
//
// Absorbed EmailPage (Staging) + CampaignsPage. EmailPage was DELETED
// 19 Sep 2026 (P1.5 IA-3b); this file is now the only owner of its surface.
//
// "Absorbs" had been claimed here since the file was written and was only
// two-thirds true: EmailPage had two tabs, and just its Outreach Sequences had
// been ported. Its Cadence Tracker — and `/api/email/cadences`, whose sole
// caller it was — had not. So the comment said the work was finished while the
// code disagreed, and the page survived as an unreachable route for months with
// a live capability inside it, still collecting bug fixes nobody could see.
// The Cadence tab below is that capability, ported (IA-3a1) and verified
// against real data before the deletion — in that order, deliberately.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './CommsPage.css';
import { apiFetch } from '../lib/api';

// ── Helpers ───────────────────────────────────────────────────────────────────
function formatDate(isoStr) {
  if (!isoStr) return '';
  const safe = (typeof isoStr === 'string' && isoStr.length === 10) ? isoStr + 'T12:00:00' : isoStr;
  return new Date(safe).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function timeAgo(isoStr) {
  if (!isoStr) return '';
  const diff  = Date.now() - new Date(isoStr).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins < 60)  return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days === 1) return 'Yesterday';
  return `${days}d ago`;
}

// ── Priority config ───────────────────────────────────────────────────────────
const PRIORITY_CONFIG = {
  Hot:        { label: 'Hot',       color: 'var(--dm-red)', bg: 'rgba(220,38,38,0.08)',   dot: '#ef4444' },
  Important:  { label: 'Important', color: 'var(--dm-amber)', bg: 'rgba(217,119,6,0.08)',   dot: '#f59e0b' },
  FYI:        { label: 'FYI',       color: 'var(--dm-blue-vivid)', bg: 'rgba(37,99,235,0.08)',   dot: 'var(--dm-blue-mid)' },
  Suppressed: { label: 'Suppressed',color: '#64748b', bg: 'rgba(100,116,139,0.08)', dot: '#94a3b8' },
};

// ── Draft status config (from EmailPage) ──────────────────────────────────────
const DRAFT_STATUS = {
  'Pending Approval': { label: 'Pending',  bg: 'var(--dm-amber-light)', color: 'var(--dm-amber-deep)', dot: '#f59e0b' },
  'Approved':         { label: 'Approved', bg: 'var(--dm-green-light)', color: '#065f46', dot: 'var(--dm-green)' },
  'Sent':             { label: 'Sent',     bg: 'var(--dm-blue-light)', color: 'var(--dm-blue-deep)', dot: 'var(--dm-blue-mid)' },
  'Rejected':         { label: 'Rejected', bg: 'var(--dm-red-light)', color: '#991b1b', dot: '#ef4444' },
  'Edited':           { label: 'Edited',   bg: 'var(--dm-purple-light)', color: '#5b21b6', dot: 'var(--dm-purple-vivid)' },
};

const TYPE_ICONS = {
  'Outreach':            '✉️',
  'Follow-up':           '🔁',
  'Meeting Request':     '📅',
  'Proposal Follow-Up':  '📋',
  'Exec Intro':          '🤝',
  'Thank You Follow-Up': '🙏',
  'Internal Summary':    '📝',
};



// ── Send helper ───────────────────────────────────────────────────────────────
async function executeSend(draft) {
  // If we have an email address, send through Graph as the signed-in user (A3).
  if (draft.toEmail) {
    const res = await apiFetch('/api/comms/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        to:      draft.toEmail,
        subject: draft.subject,
        body:    draft.body,
        draftId: draft.id,
      }),
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error || 'Send failed');
    return 'sent';
  }

  // Fallback: copy to clipboard and mark Approved
  const text = `To: ${draft.to || draft.toEmail}\nSubject: ${draft.subject}\n\n${draft.body}`;
  try { await navigator.clipboard.writeText(text); } catch { /* ignore */ }
  const res  = await apiFetch('/api/cyrano/drafts/update', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ draftId: draft.id, status: 'Approved' }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Approve failed');
  return 'approved';
}

// ════════════════════════════════════════════════════════════════════════════
// TAB 1 — INBOX
// ════════════════════════════════════════════════════════════════════════════
function InboxTab({ onNavigate, onUnreadChange }) {
  const [emails, setEmails]           = useState([]);
  const [filter, setFilter]           = useState('all');
  const [tabCounts, setTabCounts]     = useState({});   // from the API — see the note below
  const [loading, setLoading]         = useState(true);
  const [selectedEmail, setSelected]  = useState(null);
  const [actioning, setActioning]     = useState(null);
  const [composing, setComposing]     = useState(null); // null | { mode: 'reply'|'new', replyTo?, subject?, body? }
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody]       = useState('');
  const [composeTo, setComposeTo]           = useState('');
  const [cyranoLoading, setCyranoLoading]   = useState(false);
  const [staging, setStaging]               = useState(false);
  // B11 — this tab had no way to tell the user anything. DraftsTab and
  // SequencesTab each carry the same pair; .comms-toast is already styled.
  const [toast, setToast]                   = useState(null);
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3500); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await apiFetch(`/api/comms/inbox?filter=${filter}&status=active`);
      const data = await res.json();
      if (data.success) {
        setEmails(data.emails || []);
        // The TAB counts come from the server, which counts the whole mailbox. They
        // cannot be derived here: `emails` is filtered by the active tab AND capped at
        // the page limit, which is exactly how "All 11" became "All 5" on a click.
        setTabCounts(data.counts || {});
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  // TWO DIFFERENT NUMBERS, kept apart deliberately (14 Sep 2026).
  //
  // `tabCounts` comes from the server and describes the MAILBOX: every priority,
  // Suppressed included, unaffected by which tab is open or by the page limit. That is
  // what a tab label has to mean — the previous local derivation made "All" read 11 on
  // the Suppressed tab and 5 once clicked, because it counted whatever was on screen.
  //
  // `unreadCount` stays LOCAL and stays UNREAD-ONLY, because it feeds the Dashboard's
  // email badge via onUnreadChange. The server counts honour ?status=active (everything
  // not Archived), so handing those to the badge would quietly redefine it from "unread"
  // to "active" — a change to a different screen that nobody asked for.
  const unreadCount = useMemo(
    () => emails.filter(e => e.status === 'Unread' || e.status === 'New').length,
    [emails],
  );

  // Notify Dashboard when unread count changes so nav badge stays current
  useEffect(() => {
    if (onUnreadChange) onUnreadChange(unreadCount);
  }, [unreadCount, onUnreadChange]);

  const handleAction = async (emailId, action, outcome) => {
    setActioning(emailId);
    try {
      await apiFetch('/api/comms/action', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emailId, action, outcome }),
      });
      // Optimistic update — archive removes immediately; other actions update status in place
      if (action === 'archive') {
        setEmails(prev => prev.filter(e => e.id !== emailId));
      } else {
        setEmails(prev => prev.map(e =>
          e.id === emailId
            ? { ...e, status: action === 'respond' ? 'Responded' : action === 'read' ? 'Read' : e.status }
            : e
        ));
      }
      if (action === 'escalate') {
        const email = emails.find(e => e.id === emailId);
        if (email && onNavigate) {
          // Strip HTML from body for Floor context
          const rawBody = email.fullBody || email.preview || '';
          const cleanBody = rawBody
            .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
            .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
            .replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>/gi, '\n')
            .replace(/<\/div>/gi, '\n').replace(/<[^>]+>/g, '')
            .replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"')
            .replace(/[ \t]+/g, ' ').replace(/\n{3,}/g, '\n\n').trim()
            .slice(0, 3000);

          const floorMessage = `I have an email I need help responding to.

FROM: ${email.fromName || email.fromEmail} <${email.fromEmail}>
SUBJECT: ${email.subject}
${email.accountName ? 'ACCOUNT: ' + email.accountName : ''}

EMAIL CONTENT:
${cleanBody}

Please read this email carefully. Victor, what is your strategic read on this — what are they really saying and what should my response accomplish? Then have Cyrano draft a response.`;

          onNavigate('floor', null, {
            type: 'nav',
            accountId: email.accountId || null,
            accountName: email.accountName || null,
            message: floorMessage,
          });
        }
      }
    } catch { /* ignore */ }
    setActioning(null);
  };

  const filters = [
    { id: 'all',        label: 'All',          count: tabCounts.total },
    { id: 'hot',        label: '🔴 Hot',        count: tabCounts.hot },
    { id: 'important',  label: '🟡 Important',  count: tabCounts.important },
    { id: 'fyi',        label: '🔵 FYI',        count: tabCounts.fyi },
    // Was hardcoded 0, so the tab never showed a number however much noise it held —
    // and Gmail's traffic lands almost entirely here.
    { id: 'suppressed', label: 'Suppressed',  count: tabCounts.suppressed },
  ];

  return (
    <div className="comms-tab-content">
      <div className="comms-filter-bar">
        {filters.map(f => (
          <button key={f.id}
            className={`comms-filter-btn${filter === f.id ? ' active' : ''}`}
            onClick={() => setFilter(f.id)}>
            {f.label}
            {f.count > 0 && <span className="comms-filter-count">{f.count}</span>}
          </button>
        ))}
        <button className="comms-compose-btn"
          onClick={() => { setComposeTo(''); setComposeSubject(''); setComposeBody(''); setComposing({ mode: 'new' }); }}>
          ✉ Compose
        </button>
        <button className="comms-refresh-btn" onClick={load}>↺ Refresh</button>
      </div>

      {/* Compose / Reply panel */}
      {composing && (
        <div className="comms-compose-panel">
          <div className="comms-compose-header">
            <span className="comms-compose-title">{composing.mode === 'reply' ? `Reply to ${composing.replyTo?.fromName || composing.replyTo?.fromEmail}` : 'New Email'}</span>
            <button className="comms-compose-close" onClick={() => setComposing(null)}>✕</button>
          </div>
          <div className="comms-compose-fields">
            <div className="comms-compose-row">
              <label>To</label>
              <input className="comms-compose-input" value={composeTo}
                onChange={e => setComposeTo(e.target.value)} placeholder="recipient@email.com" />
            </div>
            <div className="comms-compose-row">
              <label>Subject</label>
              <input className="comms-compose-input" value={composeSubject}
                onChange={e => setComposeSubject(e.target.value)} placeholder="Subject" />
            </div>
            <textarea className="comms-compose-body" rows={8} value={composeBody}
              onChange={e => setComposeBody(e.target.value)}
              placeholder="Write your email here, or click 'Ask Cyrano' to get a draft..." />
            <div className="comms-compose-actions">
              <button className="comms-compose-btn-cyrano"
                disabled={cyranoLoading}
                onClick={async () => {
                  setCyranoLoading(true);
                  try {
                    const context = composing.replyTo
                      ? `I need to reply to this email:\nFROM: ${composing.replyTo.fromName || composing.replyTo.fromEmail}\nSUBJECT: ${composing.replyTo.subject}\nCONTENT: ${(composing.replyTo.preview || '').slice(0, 500)}`
                      : `I need to write an email to: ${composeTo}\nSUBJECT: ${composeSubject}`;
                    const res = await apiFetch('/api/cyrano/chat', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        message: `${context}\n\nPlease draft a Signal-Led, professional email. Return ONLY the email body text, no subject line, no preamble.`,
                        thread: [],
                      }),
                    });
                    const data = await res.json();
                    const draft = data.message || data.text || data.content || '';
                    if (draft) setComposeBody(draft.trim());
                  } catch { /* ignore */ } finally {
                    setCyranoLoading(false);
                  }
                }}>
                {cyranoLoading ? '✍️ Writing...' : '✍️ Ask Cyrano'}
              </button>
              <button className="comms-compose-btn-send"
                disabled={!composeTo || !composeSubject || !composeBody}
                onClick={async () => {
                  try {
                    await apiFetch('/api/comms/send', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ to: composeTo, subject: composeSubject, body: composeBody }),
                    });
                    setComposing(null);
                  } catch { /* ignore */ }
                }}>
                Send via Outlook →
              </button>
              <button className="comms-compose-btn-stage"
                disabled={!composeBody || staging}
                onClick={async () => {
                  // B11 — this posted to a handler that did not exist, discarded
                  // the 404 with `.catch(() => {})`, and closed the composer
                  // anyway. The draft was lost and the user was told it was
                  // staged. Note the `.catch` could never have helped: apiFetch
                  // RESOLVES on a 404 (src/lib/api.js returns the Response), so
                  // the only way to see the failure is to read the body.
                  setStaging(true);
                  try {
                    const res = await apiFetch('/api/cyrano/drafts/create', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        to: composeTo,
                        subject: composeSubject,
                        body: composeBody,
                        status: 'Pending Approval',
                        // A reply carries the account the inbound email was
                        // matched to; today/brief.js and drafts/list.js both key
                        // off account_id, so a staged reply that omits it lands
                        // unattached. accountName is deliberately not sent —
                        // comms/inbox.js returns it hardcoded null.
                        accountId: composing.replyTo?.accountId || null,
                        contactId: composing.replyTo?.contactId || null,
                      }),
                    });
                    const data = await res.json();
                    if (data.success) {
                      setComposing(null);
                      showToast('✅ Staged for approval');
                    } else {
                      // The composer stays OPEN. Closing it on a failure is what
                      // lost the draft in the first place.
                      showToast('⚠️ ' + (data.error || 'Could not stage this draft'));
                    }
                  } catch (err) {
                    showToast('⚠️ ' + (err.message || 'Could not stage this draft'));
                  }
                  setStaging(false);
                }}>
                {staging ? 'Staging...' : 'Send to Staging'}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading && <div className="comms-loading">Quinn is loading your inbox...</div>}

      {!loading && emails.length === 0 && (
        <div className="comms-empty">
          <div className="comms-empty-icon">📭</div>
          <div className="comms-empty-title">Inbox is clear</div>
          <div className="comms-empty-sub">
            {filter === 'all'
              ? 'No emails yet. Connect Outlook under Profile & Settings → Integrations to start receiving emails here.'
              : `No ${filter} emails.`}
          </div>
        </div>
      )}

      <div className="comms-email-list">
        {emails.map(email => {
          const pc  = PRIORITY_CONFIG[email.priority] || PRIORITY_CONFIG.FYI;
          const isSelected = selectedEmail?.id === email.id;
          return (
            <div key={email.id}
              className={`comms-email-card${isSelected ? ' expanded' : ''}${email.status === 'Archived' ? ' archived' : ''}${email.status === 'Unread' || email.status === 'New' ? ' unread' : ''}${email.status === 'Read' ? ' read' : ''}`}
              onClick={() => {
                const nowSelected = !isSelected;
                setSelected(nowSelected ? email : null);
                // Auto-mark as read when opened
                if (nowSelected && (email.status === 'Unread' || email.status === 'New')) {
                  handleAction(email.id, 'read');
                }
              }}>

              <div className="comms-email-row">
                <div className="comms-email-dot" style={{ background: pc.dot }} />
                <div className="comms-email-main">
                  <div className="comms-email-header">
                    <span className="comms-email-from">{email.fromName || email.fromEmail}</span>
                    {email.accountName && (
                      <span className="comms-email-account">· {email.accountName}</span>
                    )}
                    <span className="comms-email-time">{timeAgo(email.receivedAt)}</span>
                  </div>
                  <div className="comms-email-subject">{email.subject}</div>
                  <div className="comms-email-quinn">{email.quinnRead}</div>
                </div>
                <div className="comms-email-badges">
                  <span className="comms-priority-badge" style={{ color: pc.color, background: pc.bg }}>
                    {pc.label}
                  </span>
                  {email.requiresResponse && (
                    <span className="comms-response-badge">Reply needed</span>
                  )}
                </div>
              </div>

              {isSelected && (
                <div className="comms-email-expanded">
                  {(email.fullBody || email.preview) && (() => {
                    let text = email.fullBody || email.preview;
                    if (email.fullBody) {
                      // Strip style/script blocks first
                      text = text.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
                      text = text.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
                      // Convert block elements to newlines before stripping tags
                      text = text.replace(/<br\s*\/?>/gi, '\n');
                      text = text.replace(/<\/p>/gi, '\n');
                      text = text.replace(/<\/div>/gi, '\n');
                      text = text.replace(/<\/tr>/gi, '\n');
                      text = text.replace(/<\/li>/gi, '\n');
                      // Strip remaining tags
                      text = text.replace(/<[^>]+>/g, '');
                      // Decode entities
                      text = text.replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&')
                        .replace(/&lt;/g, '<').replace(/&gt;/g, '>')
                        .replace(/&quot;/g, '"').replace(/&#\d+;/g, '');
                      // Collapse excess whitespace but preserve intentional newlines
                      text = text.replace(/[ \t]+/g, ' ');
                      text = text.replace(/\n[ \t]+/g, '\n');
                      text = text.replace(/\n{3,}/g, '\n\n');
                      // Cut at first quoted reply marker — show only the latest message
                      const cutMarkers = [
                        /\nFrom:.*\nSent:/i,
                        /\n-+Original Message-+/i,
                        /\nOn .* wrote:/i,
                        /\n_{3,}/,
                      ];
                      for (const marker of cutMarkers) {
                        const match = text.search(marker);
                        if (match > 100) { text = text.slice(0, match).trim(); break; }
                      }
                      text = text.trim();
                    }
                    return (
                      <div className="comms-email-body">
                        <div className="comms-email-body-text">{text}</div>
                      </div>
                    );
                  })()}
                  <div className="comms-email-meta">
                    <span>From: {email.fromEmail}</span>
                    {email.classification && <span>· {email.classification}</span>}
                    <span>· {formatDate(email.receivedAt)}</span>
                  </div>
                  <div className="comms-email-actions">
                    {email.status !== 'Archived' && (
                      <>
                        <button className="comms-action-btn comms-action-reply"
                          onClick={e => {
                            e.stopPropagation();
                            setComposeTo(email.fromEmail || '');
                            setComposeSubject(`Re: ${email.subject || ''}`);
                            setComposeBody('');
                            setComposing({ mode: 'reply', replyTo: email });
                          }}>
                          ↩ Reply
                        </button>
                        <button className="comms-action-btn comms-action-archive"
                          onClick={e => { e.stopPropagation(); handleAction(email.id, 'archive'); }}
                          disabled={actioning === email.id}>
                          Archive
                        </button>
                        <button className="comms-action-btn comms-action-escalate"
                          onClick={e => { e.stopPropagation(); handleAction(email.id, 'escalate'); }}
                          disabled={actioning === email.id}>
                          Escalate to Floor →
                        </button>
                      </>
                    )}
                    <div className="comms-outcome-row">
                      <span className="comms-outcome-label">Outcome:</span>
                      {['Replied', 'Meeting Booked', 'Deal Progressed', 'No Response Needed', 'Not Relevant'].map(o => (
                        <button key={o}
                          className={`comms-outcome-btn${email.outcome === o ? ' active' : ''}`}
                          onClick={e => { e.stopPropagation(); handleAction(email.id, 'outcome', o); }}>
                          {o}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {toast && <div className="comms-toast">{toast}</div>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// TAB 2 — DRAFTS
// ════════════════════════════════════════════════════════════════════════════
function DraftsTab({ onNavigate }) {
  const [allDrafts, setAllDrafts]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [filter, setFilter]           = useState('Pending Approval');
  const [expanded, setExpanded]       = useState(null);
  const [sending, setSending]         = useState(null);
  const [toast, setToast]             = useState(null);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    // Fetch all statuses so we can filter client-side
    Promise.all([
      apiFetch('/api/cyrano/drafts/list?status=Pending Approval').then(r => r.json()),
      apiFetch('/api/cyrano/drafts/list?status=Approved').then(r => r.json()),
      apiFetch('/api/cyrano/drafts/list?status=Rejected').then(r => r.json()),
    ]).then(([pending, approved, rejected]) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const all = [
        ...(pending.drafts || []),
        ...(approved.drafts || []),
        ...(rejected.drafts || []),
      ].filter(d => {
        // Only show drafts created today or newer
        if (!d.createdAt) return true;
        return new Date(d.createdAt) >= today;
      });
      setAllDrafts(all);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const drafts = filter === 'All'
    ? allDrafts
    : allDrafts.filter(d => d.status === filter);

  const handleSend = async (draft) => {
    setSending(draft.id);
    try {
      const result = await executeSend(draft);
      const newStatus = result === 'sent' ? 'Sent' : 'Approved';
      setAllDrafts(prev => prev.map(d => d.id === draft.id ? { ...d, status: newStatus } : d));
      showToast(result === 'sent' ? '✅ Sent via Outlook' : 'Draft approved — copied to clipboard');
    } catch (err) {
      showToast('Error: ' + err.message);
    }
    setSending(null);
  };

  const handleReject = async (draftId) => {
    await apiFetch('/api/cyrano/drafts/update', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ draftId, status: 'Rejected' }),
    });
    setAllDrafts(prev => prev.map(d => d.id === draftId ? { ...d, status: 'Rejected' } : d));
  };

  const statusFilters = ['Pending Approval', 'Approved', 'Sent', 'Rejected', 'All'];
  const pendingCount  = allDrafts.filter(d => d.status === 'Pending Approval' || d.status === 'Edited').length;

  return (
    <div className="comms-tab-content">
      {toast && <div className="comms-toast">{toast}</div>}
      <div className="comms-drafts-header">
        <div className="comms-filter-bar">
          {statusFilters.map(s => (
            <button key={s}
              className={`comms-filter-btn${filter === s ? ' active' : ''}`}
              onClick={() => setFilter(s)}>
              {s}
              {s === 'Pending Approval' && pendingCount > 0 && (
                <span className="comms-filter-count">{pendingCount}</span>
              )}
            </button>
          ))}
        </div>
        <button className="comms-new-btn"
          onClick={() => onNavigate && onNavigate('floor', null, {
            type: 'nav', message: 'Cyrano — I want to draft new outreach. Let\'s start with who I\'m targeting.',
          })}>
          + New Draft →
        </button>
      </div>

      {loading && <div className="comms-loading">Loading drafts...</div>}

      {!loading && drafts.length === 0 && (
        <div className="comms-empty">
          <div className="comms-empty-icon">✍️</div>
          <div className="comms-empty-title">No {filter === 'All' ? '' : filter.toLowerCase()} drafts</div>
          <div className="comms-empty-sub">Ask Cyrano on The Floor to draft outreach — it'll appear here.</div>
        </div>
      )}

      <div className="comms-draft-list">
        {drafts.map((draft, i) => {
          const cfg  = DRAFT_STATUS[draft.status] || DRAFT_STATUS['Pending Approval'];
          const icon = TYPE_ICONS[draft.emailType] || '✉️';
          const isExp = expanded === (draft.id || i);
          return (
            <div key={draft.id || i} className="comms-draft-card">
              <div className="comms-draft-row" onClick={() => setExpanded(isExp ? null : (draft.id || i))}>
                <span className="comms-draft-icon">{icon}</span>
                <div className="comms-draft-main">
                  <div className="comms-draft-subject">{draft.subject || 'Untitled draft'}</div>
                  <div className="comms-draft-meta">
                    {draft.accountName && <span>{draft.accountName}</span>}
                    {draft.to && <span>· {draft.to}</span>}
                    {draft.createdAt && <span>· {formatDate(draft.createdAt)}</span>}
                  </div>
                </div>
                <span className="comms-draft-status" style={{ color: cfg.color, background: cfg.bg }}>
                  <span style={{ background: cfg.dot, width: 6, height: 6, borderRadius: '50%', display: 'inline-block', marginRight: 5 }} />
                  {cfg.label}
                </span>
              </div>

              {isExp && (
                <div className="comms-draft-body">
                  {draft.body && <pre className="comms-draft-text">{draft.body}</pre>}
                  {(draft.status === 'Pending Approval' || draft.status === 'Edited') && (
                    <div className="comms-draft-actions">
                      <button className="comms-action-btn comms-action-respond"
                        onClick={() => handleSend(draft)}
                        disabled={sending === draft.id}>
                        {sending === draft.id ? (draft.toEmail ? 'Sending...' : 'Approving...') : (draft.toEmail ? '✓ Send via Outlook' : '✓ Approve & Copy')}
                      </button>
                      <button className="comms-action-btn"
                        onClick={() => onNavigate && onNavigate('floor', null, {
                          type: 'account', accountId: draft.accountId, accountName: draft.accountName,
                          message: `Cyrano — I want to review and revise this draft: "${draft.subject}"`,
                        })}>
                        Edit in Floor →
                      </button>
                      <button className="comms-action-btn comms-action-archive"
                        onClick={() => handleReject(draft.id)}>
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// TAB 3 — SEQUENCES (Cadence Tracker from EmailPage)
// ════════════════════════════════════════════════════════════════════════════
function SequencesTab({ onNavigate }) {
  const [drafts,    setDrafts]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [expanded,  setExpanded]  = useState(null);
  const [sending,   setSending]   = useState(null);
  const [editEmail, setEditEmail] = useState({});
  const [toast,     setToast]     = useState(null);
  const [filter,    setFilter]    = useState('Pending Approval');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3500); };

  const load = () => {
    setLoading(true);
    Promise.all([
      apiFetch('/api/cyrano/drafts/list?status=Pending Approval').then(r => r.json()),
      apiFetch('/api/cyrano/drafts/list?status=Approved').then(r => r.json()),
      apiFetch('/api/cyrano/drafts/list?status=Sent').then(r => r.json()),
    ]).then(([pending, approved, sent]) => {
      setDrafts([
        ...(pending.drafts || []),
        ...(approved.drafts || []),
        ...(sent.drafts || []),
      ]);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filtered = filter === 'All'
    ? drafts
    : drafts.filter(d => d.status === filter);

  const handleSend = async (draft) => {
    const toEmail = editEmail[draft.id] || draft.toEmail;
    if (!toEmail) {
      showToast('⚠️ Add recipient email address before sending');
      return;
    }
    setSending(draft.id);
    try {
      const res = await apiFetch('/api/comms/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to:      toEmail,
          subject: draft.subject,
          body:    draft.body,
          draftId: draft.id,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setDrafts(prev => prev.map(d => d.id === draft.id ? { ...d, status: 'Sent', toEmail } : d));
        showToast('✅ Sent to ' + toEmail);
      } else {
        showToast('⚠️ ' + (data.error || 'Send failed'));
      }
    } catch (err) {
      showToast('⚠️ ' + err.message);
    }
    setSending(null);
  };

  const handleApprove = async (draft) => {
    await apiFetch('/api/cyrano/drafts/update', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ draftId: draft.id, status: 'Approved' }),
    });
    setDrafts(prev => prev.map(d => d.id === draft.id ? { ...d, status: 'Approved' } : d));
  };

  const handleReject = async (draftId) => {
    await apiFetch('/api/cyrano/drafts/update', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ draftId, status: 'Rejected' }),
    });
    setDrafts(prev => prev.filter(d => d.id !== draftId));
  };

  const STATUS_FILTERS = [
    { id: 'Pending Approval', label: 'Pending', count: drafts.filter(d => d.status === 'Pending Approval').length },
    { id: 'Approved',         label: 'Approved', count: drafts.filter(d => d.status === 'Approved').length },
    { id: 'Sent',             label: 'Sent',    count: drafts.filter(d => d.status === 'Sent').length },
    { id: 'All',              label: 'All',     count: drafts.length },
  ];

  return (
    <div className="comms-tab-content">
      {toast && <div className="comms-toast">{toast}</div>}

      <div className="comms-sequences-header">
        <div className="comms-sequences-desc">
          Cyrano-drafted outreach staged here for review and send. Add recipient email, approve, and send via Outlook.
        </div>
        <button className="comms-new-btn"
          onClick={() => onNavigate && onNavigate('floor', null, {
            type: 'nav',
            message: "Cyrano — I want to draft a new outreach sequence for a specific person at an account.",
          })}>
          + Draft in Floor →
        </button>
      </div>

      <div className="comms-filter-bar">
        {STATUS_FILTERS.map(f => (
          <button key={f.id}
            className={`comms-filter-btn${filter === f.id ? ' active' : ''}`}
            onClick={() => setFilter(f.id)}>
            {f.label}
            {f.count > 0 && <span className="comms-filter-count">{f.count}</span>}
          </button>
        ))}
      </div>

      {loading && <div className="comms-loading">Loading drafts...</div>}

      {!loading && filtered.length === 0 && (
        <div className="comms-empty">
          <div className="comms-empty-icon">✍️</div>
          <div className="comms-empty-title">No {filter === 'All' ? '' : filter.toLowerCase()} drafts</div>
          <div className="comms-empty-sub">Ask Cyrano on The Floor to draft outreach for an account.</div>
        </div>
      )}

      <div className="comms-sequence-list">
        {filtered.map(draft => {
          const isOpen = expanded === draft.id;
          const recipientEmail = editEmail[draft.id] !== undefined ? editEmail[draft.id] : (draft.toEmail || '');
          const canSend = draft.status !== 'Sent';

          return (
            <div key={draft.id} className={`comms-sequence-card${isOpen ? ' comms-sequence-card--open' : ''}`}>
              <div className="comms-sequence-row" onClick={() => setExpanded(isOpen ? null : draft.id)}>
                <div className="comms-sequence-main">
                  <div className="comms-sequence-account">{draft.accountName || 'Unknown Account'}</div>
                  <div className="comms-sequence-subject">{draft.subject}</div>
                  <div className="comms-sequence-meta">
                    {draft.to && <span>→ {draft.to}</span>}
                    {draft.draftType && <span>· {draft.draftType}</span>}
                    {draft.createdAt && <span>· {formatDate(draft.createdAt)}</span>}
                  </div>
                </div>
                <div className="comms-sequence-right">
                  <span className={`comms-draft-status comms-draft-status--${(draft.status || '').toLowerCase().replace(' ', '-')}`}>
                    {draft.status}
                  </span>
                  <span className="comms-sequence-chevron">{isOpen ? '↑' : '↓'}</span>
                </div>
              </div>

              {isOpen && (
                <div className="comms-sequence-expanded">
                  <div className="comms-sequence-body">{draft.body}</div>

                  {canSend && (
                    <div className="comms-sequence-send-row">
                      <input
                        className="comms-email-input"
                        type="email"
                        placeholder="Recipient email address"
                        value={recipientEmail}
                        onChange={e => setEditEmail(prev => ({ ...prev, [draft.id]: e.target.value }))}
                        onClick={e => e.stopPropagation()}
                      />
                      <div className="comms-sequence-actions">
                        {draft.status === 'Pending Approval' && (
                          <button className="comms-action-btn comms-action-approve"
                            onClick={e => { e.stopPropagation(); handleApprove(draft); }}>
                            ✓ Approve
                          </button>
                        )}
                        <button className="comms-action-btn comms-action-send"
                          disabled={sending === draft.id || !recipientEmail}
                          onClick={e => { e.stopPropagation(); handleSend(draft); }}>
                          {sending === draft.id ? '⏳ Sending...' : '📤 Send via Outlook'}
                        </button>
                        <button className="comms-action-btn comms-action-reject"
                          onClick={e => { e.stopPropagation(); handleReject(draft.id); }}>
                          ✕
                        </button>
                      </div>
                    </div>
                  )}

                  {draft.status === 'Sent' && (
                    <div className="comms-sequence-sent-note">
                      ✅ Sent to {draft.toEmail || recipientEmail}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// TAB 4 — CAMPAIGNS
// ════════════════════════════════════════════════════════════════════════════
function CampaignsTab({ onNavigate }) {
  return (
    <div className="comms-tab-content">
      <div className="comms-campaigns-hero">
        <div className="comms-campaigns-hero-icon">📣</div>
        <div className="comms-campaigns-hero-title">Campaigns</div>
        <div className="comms-campaigns-hero-desc">
          Campaigns are coordinated outreach to multiple contacts — tradeshow follow-ups,
          product releases, event invitations, and industry announcements.
          <br /><br />
          Unlike Sequences (one person, multiple touches), a Campaign targets a list
          of contacts with a coordinated message strategy built by Cyrano.
        </div>
        <div className="comms-campaigns-hero-examples">
          <div className="comms-campaigns-example">🎪 Tradeshow follow-up to 20+ booth visitors</div>
          <div className="comms-campaigns-example">🚀 New service announcement to target accounts</div>
          <div className="comms-campaigns-example">📋 RFP season outreach to procurement contacts</div>
          <div className="comms-campaigns-example">🤝 Conference networking follow-up blast</div>
        </div>
        <div className="comms-campaigns-hero-note">
          Campaign builder coming next session — Cyrano will build the target list,
          message strategy, and individual touches.
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// TAB 5 — SENT
// ════════════════════════════════════════════════════════════════════════════
function SentTab({ onNavigate }) {
  const [drafts, setDrafts]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch('/api/cyrano/drafts/list')
      .then(r => r.json())
      .then(d => {
        const sent = (d.drafts || []).filter(dr => dr.status === 'Sent' || dr.status === 'Approved');
        setDrafts(sent);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  return (
    <div className="comms-tab-content">
      {loading && <div className="comms-loading">Loading sent emails...</div>}

      {!loading && drafts.length === 0 && (
        <div className="comms-empty">
          <div className="comms-empty-icon">📤</div>
          <div className="comms-empty-title">No sent emails yet</div>
          <div className="comms-empty-sub">Approved and sent drafts will appear here.</div>
        </div>
      )}

      <div className="comms-draft-list">
        {drafts.map((draft, i) => {
          const cfg  = DRAFT_STATUS[draft.status] || DRAFT_STATUS['Approved'];
          const icon = TYPE_ICONS[draft.emailType] || '✉️';
          return (
            <div key={draft.id || i} className="comms-draft-card">
              <div className="comms-draft-row">
                <span className="comms-draft-icon">{icon}</span>
                <div className="comms-draft-main">
                  <div className="comms-draft-subject">{draft.subject || 'Untitled'}</div>
                  <div className="comms-draft-meta">
                    {draft.accountName && <span>{draft.accountName}</span>}
                    {draft.to && <span>· {draft.to}</span>}
                    {draft.createdAt && <span>· {formatDate(draft.createdAt)}</span>}
                  </div>
                </div>
                <span className="comms-draft-status" style={{ color: cfg.color, background: cfg.bg }}>
                  {cfg.label}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// TAB 6 — CADENCE (ported from EmailPage's Cadence Tracker, P1.5 IA-3a1)
//
// This is the COVERAGE view: every account and where it stands against its
// contact cadence. Command Center's Cold Accounts block answers a narrower
// question from `api/accounts/cold.js` — it returns ONLY overdue accounts,
// ranked by an urgency score. Two capabilities live here and nowhere else in
// the app, and they are why this was ported rather than dropped:
//
//   - `due-soon`  — warns BEFORE an account goes overdue (cold.js cannot: it
//                   filters out anything under the threshold).
//   - nextPlanned — the next scheduled touch, read from `quinn_calendar`.
//
// Endpoint is unchanged: /api/email/cadences already resolves the caller with
// getRequestContext and sorts worst-first, so this is a UI port.
// ════════════════════════════════════════════════════════════════════════════

// Order matches the endpoint's own sort (cadences.js ORDER), so the filter row
// reads in the same direction as the list beneath it.
// Tokens, not hex — var(--dm-*) resolves fine inside a style prop, and
// tests/css-raw-hex.test.js ratchets the JSX hex count downward. The sibling
// DRAFT_STATUS map above is the shape to match.
const CADENCE_STATUS = {
  'overdue':    { label: 'Overdue',    color: 'var(--dm-red-deep)',    bg: 'var(--dm-red-light)' },
  'due-soon':   { label: 'Due soon',   color: 'var(--dm-amber-deep)',  bg: 'var(--dm-amber-light)' },
  'no-contact': { label: 'No contact', color: 'var(--dm-indigo-deep)', bg: 'var(--dm-indigo-tint)' },
  'on-track':   { label: 'On track',   color: 'var(--dm-green-deep)',  bg: 'var(--dm-green-light)' },
  'as-needed':  { label: 'As needed',  color: 'var(--dm-text-muted)',  bg: 'var(--dm-surface-hover)' },
};

function CadenceTab({ onNavigate }) {
  const [cadences, setCadences] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [filter,   setFilter]   = useState('attention');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/api/email/cadences');
        // apiFetch RESOLVES on a 404/500 — there is no rejection to catch, so
        // the status has to be read explicitly or a dead endpoint renders as
        // "no accounts" forever (CLAUDE.md; B11 shipped for months on this).
        if (!res.ok) throw new Error(`Cadence request failed (${res.status})`);
        const data = await res.json();
        if (cancelled) return;
        setCadences(data.cadences || []);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Could not load cadence status');
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // 'attention' is the default because the whole point of the view is the
  // states that need action; 'all' is one click away for the coverage read.
  //
  // no-contact IS attention. The first cut of this left it out, and against the
  // real book — where every account was never-contacted — the tab rendered
  // "Every account is on track" over 10 accounts nobody had ever called. That
  // is the worst failure this view can have: it is the one screen whose job is
  // to say "you are not covering these". api/accounts/cold.js has always agreed
  // (`daysSince = 999 // never touched = maximum cold`, :70) and surfaces the
  // same rows in Command Center, so excluding them here also put two surfaces
  // over one dataset into direct contradiction.
  const ATTENTION_STATES = ['overdue', 'due-soon', 'no-contact'];
  const needsAttention = cadences.filter(c => ATTENTION_STATES.includes(c.status));
  const filtered =
    filter === 'all'       ? cadences :
    filter === 'attention' ? needsAttention :
                             cadences.filter(c => c.status === filter);

  const countFor = (id) =>
    id === 'all'       ? cadences.length :
    id === 'attention' ? needsAttention.length :
                         cadences.filter(c => c.status === id).length;

  const FILTERS = [
    { id: 'attention',  label: 'Needs attention' },
    { id: 'all',        label: 'All accounts' },
    { id: 'overdue',    label: 'Overdue' },
    { id: 'due-soon',   label: 'Due soon' },
    { id: 'no-contact', label: 'No contact' },
    { id: 'on-track',   label: 'On track' },
  ];

  return (
    <div className="comms-tab-content">
      {loading && <div className="comms-loading">Loading cadence status...</div>}

      {!loading && error && (
        <div className="comms-empty">
          <div className="comms-empty-icon">⚠️</div>
          <div className="comms-empty-title">Could not load cadence status</div>
          <div className="comms-empty-sub">{error}</div>
        </div>
      )}

      {!loading && !error && (
        <>
          <div className="comms-filter-bar">
            {FILTERS.map(f => {
              const count = countFor(f.id);
              return (
                <button key={f.id}
                  className={`comms-filter-btn${filter === f.id ? ' active' : ''}`}
                  onClick={() => setFilter(f.id)}>
                  {f.label}
                  {count > 0 && <span className="comms-filter-count">{count}</span>}
                </button>
              );
            })}
          </div>

          {filtered.length === 0 ? (
            <div className="comms-empty">
              {/* "Every account is on track" is only true when there are
                  accounts AND none of them needs attention. With an empty book
                  it is a lie of the most misleading kind — it reports coverage
                  over nothing. Distinguish the two. */}
              <div className="comms-empty-icon">
                {cadences.length === 0 ? '📭' : filter === 'attention' ? '✅' : '🔍'}
              </div>
              <div className="comms-empty-title">
                {cadences.length === 0
                  ? 'No accounts to track yet'
                  : filter === 'attention'
                    ? 'Every account is on track'
                    : 'No accounts in this state'}
              </div>
              <div className="comms-empty-sub">
                {cadences.length === 0
                  ? 'Cadence status appears here once you have accounts with a contact cadence set.'
                  : filter === 'attention'
                    ? 'Nothing is overdue, due soon, or uncontacted. This is a result, not an empty screen.'
                    : 'Try a different filter to see the rest of your book.'}
              </div>
            </div>
          ) : (
            <div className="comms-cadence-list">
              {filtered.map((c, i) => {
                const cfg = CADENCE_STATUS[c.status] || CADENCE_STATUS['on-track'];
                return (
                  <div key={c.id || i} className="comms-cadence-card"
                       onClick={() => c.id && onNavigate && onNavigate('account-detail', c.id)}>
                    <div className="comms-cadence-row">
                      <div className="comms-cadence-main">
                        <div className="comms-cadence-name">{c.accountName}</div>
                        <div className="comms-cadence-meta">
                          <span>{c.contactCadence}</span>
                          {c.tier && <span>· {c.tier}</span>}
                          {/* daysSinceLast is null for a never-touched account —
                              distinct from 0, so the check is explicit. */}
                          {c.daysSinceLast != null
                            ? <span>· last touch {c.daysSinceLast}d ago</span>
                            : <span>· never contacted</span>}
                          {c.nextPlanned?.date && (
                            <span className="comms-cadence-next">
                              · next {formatDate(c.nextPlanned.date)}
                            </span>
                          )}
                        </div>
                      </div>
                      <span className="comms-cadence-status"
                            style={{ color: cfg.color, background: cfg.bg }}>
                        {cfg.label}
                        {c.status === 'overdue' && c.overdueBy > 0 && ` · ${c.overdueBy}d`}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// MAIN CommsPage
// ════════════════════════════════════════════════════════════════════════════
// initialTab follows CyranoPage's shape (`initialTab`, defaulted in useState).
// Added 19 Sep 2026 for P1.5 IA-1c: MeetingPage's "Review Drafts →" button
// called onNavigate('drafts'), but 'drafts' is a TAB ID here, not a route — so
// it hit `default:` and landed on the dashboard. Landing on Comms' Inbox would
// be an improvement and still wrong; the button names the Drafts tab.
export default function CommsPage({ onNavigate, onUnreadChange, initialTab }) {
  const [activeTab, setActiveTab] = useState(initialTab || 'inbox');

  const tabs = [
    { id: 'inbox',     label: '📬 Inbox',     component: <InboxTab onNavigate={onNavigate} onUnreadChange={onUnreadChange} /> },
    { id: 'drafts',    label: '✍️ Drafts',     component: <DraftsTab onNavigate={onNavigate} /> },
    { id: 'sequences', label: '📋 Sequences',  component: <SequencesTab onNavigate={onNavigate} /> },
    { id: 'campaigns', label: '📣 Campaigns',  component: <CampaignsTab onNavigate={onNavigate} /> },
    { id: 'sent',      label: '📤 Sent',       component: <SentTab onNavigate={onNavigate} /> },
    { id: 'cadence',   label: '🔄 Cadence',    component: <CadenceTab onNavigate={onNavigate} /> },
  ];

  return (
    <div className="comms-page">
      <div className="comms-header">
        <div className="comms-header-left">
          <div className="comms-title">💬 Comms</div>
          <div className="comms-sub">Quinn's communications command center</div>
        </div>
      </div>

      <div className="comms-tabs">
        {tabs.map(tab => (
          <button key={tab.id}
            className={`comms-tab-btn${activeTab === tab.id ? ' active' : ''}`}
            onClick={(e) => {
              setActiveTab(tab.id);
              // Same as Cyrano: the row scrolls on narrow viewports, so centre
              // the tab that was just chosen.
              e.currentTarget.scrollIntoView?.({ block: 'nearest', inline: 'center' });
            }}>
            {tab.label}
          </button>
        ))}
      </div>

      {tabs.find(t => t.id === activeTab)?.component}
    </div>
  );
}
